import axios from '@fs/zion-axios'
import { getSessionId } from '@fs/zion-session'
import { i18n } from '@fs/zion-locale'

/**
 * Remove null values from an object
 * @param {Object} obj Object to remove null values from
 * @returns {Object} Object with null values removed
 */
const removeNulls = (obj) => {
  const newObj = {}
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== null) {
      newObj[key] = obj[key]
    }
  })
  return newObj
}

/*
 * TODO: Make sure the page always fails gracefully
 * Our team typically handles errors at the UI level (useEffects that call these functions, usually) and doesn't catch errors in here, just
 * to re-throw them. How useful is it, really? If we want to fail gracefully for the users, the UI seems like the right place to handle it.
 * https://github.com/fs-webdev/search-react/pull/3059#discussion_r1287752297
 */

/**
 * Get image data from the search-filmdata API
 * @param {string} url ARK
 * @param {string} cat Catalog
 * @param {AbortController} abortController AbortController
 * @returns {Promise<any>} Film data
 */
export async function getImageData({ url, cat, abortController, newFilmdataEndpoint }) {
  // keep query params so that the waypoint context works as expected
  const imageURL = `${url.replace(/http:\/\/localhost:500[0-9]/, 'https://www.familysearch.org')}`
  return axios.post(
    newFilmdataEndpoint ? '/search/filmdatainfo/image-data' : `/search/filmdata/filmdatainfo?type=image-data`,
    {
      type: 'image-data',
      args: {
        imageURL,
        locale: i18n.language,
        state: removeNulls({
          cat,
          catalogContext: cat,
          imageOrFilmUrl: '',
          selectedImageIndex: -1,
          viewMode: 'i',
        }),
      },
    },
    { signal: abortController?.signal }
  )
}

/**
 * Get film data from the search-filmdata API. This data is only called out for if the intiial 'image-data' type
 * response had 12 or more persons index entries (this call gets the rest of them).
 * @param {Object} imageDataResponse - response from the search-filmdata API
 * @param {string} cat - Catalog
 * @param {AbortController} abortController - AbortController
 * @returns {Promise<Object>} - response from the search-filmdata API
 */
export async function getImageIndexData({ imageDataResponse, cat, abortController, newFilmdataEndpoint }) {
  try {
    const response = await axios.post(
      newFilmdataEndpoint
        ? `/search/filmdatainfo/image-data-indexed-records`
        : `/search/filmdata/filmdatainfo?type=image-data-indexed-records`,
      {
        type: 'image-data-indexed-records',
        args: {
          imageData: imageDataResponse.data,
          locale: i18n.language,
          state: removeNulls({
            cat,
            catalogContext: cat,
            imageOrFilmUrl: '',
            selectedImageIndex: -1,
            viewMode: 'i',
          }),
        },
      },
      { signal: abortController?.signal }
    )

    return Promise.resolve(response)
  } catch (e) {
    // TODO: look into the possibility not making this request if image index count > 12
    if (e?.response?.status === 413) {
      /*
       * HTTP status 413 - payload too large, fall back to initial imageDataResponse.
       * So far, this problem has only been observed in the case of one large GenealogyBank image (and next images):
       *   https://www.familysearch.org/ark:/61903/3:1:3Q9M-CSLQ-CS9X-4
       * And when we fall back to the initial imageDataResponse, the image index actually seems complete (instead of
       * only having 12 index entries, the first image index data has all 154 index entries!).
       */
      return Promise.resolve(imageDataResponse)
    }
    return Promise.reject(e)
  }
}

/**
 * Get waypoint data from the search-filmdata API
 * @param {string} url ARK
 * @param {string} dgsNum DGS number
 * @param {AbortController} abortController AbortController
 * @returns {Promise<any>} Waypoint data
 */
export async function getWaypointData({ url, dgsNum, abortController, newFilmdataEndpoint }) {
  return axios.post(
    newFilmdataEndpoint ? `/search/filmdatainfo/waypoint-data` : `/search/filmdata/filmdatainfo?type=waypoint-data`,
    {
      type: 'waypoint-data',
      args: {
        dgsNum,
        waypointURL: `${url.replace(/https:\/\/familysearch.org/, 'https://www.familysearch.org')}`,
        locale: i18n.language,
        state: {
          imageOrFilmUrl: '',
          selectedImageIndex: -1,
          viewMode: 'i',
        },
      },
    },
    { signal: abortController?.signal }
  )
}

/**
 * Get film data from the search-filmdata API
 * @param {string} dgsNum DGS number
 * @param {string} cat Catalog
 * @param {string} ark ARK url
 * @param {number} index Image index
 * @param {boolean} loggedIn Is the user logged in
 * @param {AbortController} abortController AbortController
 * @returns {Promise<any>} Film data
 */
export async function getFilmData({ dgsNum, cat, url, index = -1, loggedIn, abortController, newFilmdataEndpoint }) {
  return axios.post(
    newFilmdataEndpoint ? `/search/filmdatainfo/film-data` : `/search/filmdata/filmdatainfo?type=film-data`,
    {
      type: 'film-data',
      loggedIn,
      sessionId: getSessionId(),
      args: {
        dgsNum,
        locale: i18n.language,
        state: {
          cat,
          imageOrFilmUrl: url,
          catalogContext: cat,
          viewMode: 'i',
          selectedImageIndex: index,
        },
      },
    },
    { signal: abortController?.signal }
  )
}

/**
 * Get film data from the search-filmdata API
 * @param {string} dgsNum DGS number
 * @param {string} cat Catalog
 * @param {string} imageIndex Image index
 * @param {boolean} loggedIn Is the user logged in
 * @param {AbortController} abortController AbortController
 * @returns {Promise<any>} Film data
 */
export async function getDgsFilmData({ dgsNum, cat, imageIndex, loggedIn, abortController, newFilmdataEndpoint }) {
  return axios.post(
    newFilmdataEndpoint ? `/search/filmdatainfo/film-data` : `/search/filmdata/filmdatainfo?type=dgs-film-data`,
    {
      type: 'film-data',
      loggedIn,
      sessionId: getSessionId(),
      args: {
        dgsNum: `${dgsNum}`,
        state: {
          i: imageIndex,
          cc: cat,
          imageOrFilmUrl: `/search/film/${dgsNum}`,
          collectionContext: cat,
          viewMode: 'g',
          selectedImageIndex: imageIndex,
        },
        locale: i18n.language,
      },
    },
    { signal: abortController?.signal }
  )
}
